import moment from "moment";
import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import { useQuery, useMutation } from "@apollo/client";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";

import OrdersRow from "./OrdersRow";
import NovaPoshtaDialog from "./NovaPoshtaDialog";
import OrdersEditableRow from "./OrdersEditableRow";
import {
	emptyOrder,
	ordersTableColumns,
	orderStatusTranslationConformity,
	deliveryTypeTranslationConformity,
} from "./common";
import CommonDialog from "../common-dialogs/DeleteDialog";
import { OrderListHead } from "../sections/@dashboard/orders";
import {
	GET_ORDERS,
	DELETE_ORDER,
	UPDATE_ORDER,
	CREATE_ORDER,
	NOVA_POSHTA_STATUSES,
} from "../graphql/orders";
import { GET_USERS_FOR_SELECT } from "../graphql/users";
import { GET_WAREHOUSES } from "../graphql/warehouses";
import { UserRoles } from "../employee-page/user-roles.enum";

const prepareGetOrdersVariables = ({
	onlyMy,
	archive,
	search,
	status,
	manager,
	warehouse,
	deliveryType,
	rowsPerPage,
	after,
}) => {
	return {
		input: {
			onlyMy,
			archive: archive ? archive : false,
			search: search ? search : "",
			statuses: status ? [status] : [],
			warehouses: warehouse ? [warehouse] : [],
			managers: manager ? [manager] : [],
			deliveries: deliveryType ? [deliveryType] : [],
			pagination: {
				first: rowsPerPage,
				after: after,
			},
			orderBy: {
				field: "CREATED_AT",
				direction: "DESC",
			},
		},
	};
};

export default function OrderPage({ authScope }) {
	const [archive, setArchive] = React.useState(false);
	const [onlyMy, setOnlyMy] = React.useState(false);
	const [search, setSearch] = React.useState("");
	const [warehouse, setWarehouse] = React.useState("");
	const [user, setUser] = React.useState(null);
	const [status, setStatus] = React.useState("");
	const [deliveryType, setDeliveryType] = React.useState("");

	const [page, setPage] = React.useState(0);
	const [after, setAfter] = React.useState(null);
	const [rowsPerPage, setRowsPerPage] = React.useState(25);

	const { loading, error, data, refetch } = useQuery(GET_ORDERS, {
		variables: prepareGetOrdersVariables({
			archive,
			search,
			status,
			manager: user?.id,
			warehouse,
			deliveryType,
			rowsPerPage,
			after: null,
		}),
	});

	const {
		loading: getNovaPoshtaStatusesLoading,
		error: getNovaPoshtaStatusesError,
		data: getNovaPoshtaStatusesData,
		refetch: getNovaPoshtaStatusesRefetch,
	} = useQuery(NOVA_POSHTA_STATUSES, {
		variables: {
			input: { npCodes: [] },
		},
	});
	const {
		loading: getUsersForSelectLoading,
		error: getUsersForSelectError,
		data: getUsersForSelectData,
		refetch: getUsersForSelectRefetch,
	} = useQuery(GET_USERS_FOR_SELECT, {
		variables: {
			input: {},
		},
	});
	const {
		loading: getWarehousesLoading,
		error: getWarehousesError,
		data: getWarehousesData,
	} = useQuery(GET_WAREHOUSES);
	const [
		createOrder,
		{
			loading: createOrderLoading,
			error: createOrderError,
			data: createOrderData,
		},
	] = useMutation(CREATE_ORDER);
	const [
		updateOrder,
		{
			loading: updateOrderLoading,
			error: updateOrderError,
			data: updateOrderData,
		},
	] = useMutation(UPDATE_ORDER);
	const [
		deleteOrder,
		{
			loading: deleteOrderLoading,
			error: deleteOrderError,
			data: deleteOrderData,
		},
	] = useMutation(DELETE_ORDER);

	const refetchOrders = async (after = (page * rowsPerPage - 1).toString()) =>
		refetch(
			prepareGetOrdersVariables({
				onlyMy,
				search,
				status,
				archive,
				manager: user?.id,
				warehouse,
				deliveryType,
				rowsPerPage,
				after: after === "-1" ? null : after,
			})
		);

	React.useEffect(() => {
		setPage(0);
		refetchOrders(null);
	}, [status, archive, search, deliveryType, warehouse, user, onlyMy]);

	const [addingNewOrder, setAddingNewOrder] = React.useState(false);
	const [editableRowId, setEditableRowId] = React.useState(null);
	const isEditableRow = (id) => {
		return editableRowId === id;
	};
	const startEditingRow = (id) => {
		if (!id || editableRowId) return;
		setEditableRowId(() => id);
	};
	const cancelEditingRow = (id) => {
		if (id !== editableRowId) return;
		if (id === emptyOrder.id) setAddingNewOrder(false);
		setEditableRowId(null);
	};

	const saveRow = async (orderData) => {
		const isEmptyOrder = orderData.id === emptyOrder.id;
		if (editableRowId !== orderData.id) return;
		if (isEmptyOrder) {
			await createOrder({
				variables: {
					input: {
						...orderData,
						id: undefined,
						managerUserId: undefined,
						archived: undefined,
						managerId: orderData.managerUserId,
						date: moment().format("DD.MM.YYYY	 hh:mm"),
					},
				},
			});
		} else {
			const updateOrderResult = await updateOrder({
				variables: {
					input: { ...orderData },
				},
			});
		}

		setPage(0);
		await refetchOrders(null);
		setEditableRowId(null);
		if (isEmptyOrder) {
			setAddingNewOrder(false);
		}
	};

	const [deleteRowId, setDeleteRowId] = React.useState(null);
	const tryDeleteRow = (id) => {
		if (deleteRowId) return;
		setDeleteRowId(id);
	};
	const deleteRow = async () => {
		await deleteOrder({
			variables: {
				id: deleteRowId,
			},
		});
		setDeleteRowId(null);
		// setEditableRowId(null);
		await refetchOrders();
	};

	const [isShowNPModal, setIsShowNPModal] = React.useState(false);
	const showNPModal = (codes) => {
		setIsShowNPModal(true);
		getNovaPoshtaStatusesRefetch({
			input: {
				npCodes: codes,
			},
		});
	};

	const isLoading = () =>
		loading ||
		deleteOrderLoading ||
		createOrderLoading ||
		updateOrderLoading ||
		getWarehousesLoading ||
		getUsersForSelectLoading;

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
		refetchOrders((newPage * rowsPerPage - 1).toString());
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(+event.target.value);
		refetchOrders(null);
		setPage(0);
	};

	return (
		<>
			<div>
				<Paper
					sx={{
						padding: 2,
					}}
				>
					<Typography variant="h3" gutterBottom align="center">
						Список накладних
					</Typography>
					<Box>
						<Box
							style={{
								display: "flex",
								justifyContent: "flex-start",
								alignItems: "center",
								margin: 10,
								gap: 15,
							}}
						>
							<div>
								<TextField
									id="outlined-basic"
									label="Пошук за номером накладної"
									variant="outlined"
									sx={{ width: 260 }}
									value={search}
									onChange={(event) => setSearch(event.target.value)}
								/>
							</div>
							<div style={{ minWidth: 120 }}>
								<FormControl fullWidth>
									<InputLabel id="demo-simple-select-label">Статус</InputLabel>
									<Select
										labelId="demo-simple-select-label"
										id="status-select"
										value={status}
										label="Статус"
										onChange={(event) => setStatus(event.target.value)}
									>
										<MenuItem key="emptyitem" value="">
											ВСІ
										</MenuItem>
										{Object.keys(orderStatusTranslationConformity).map((s) => (
											<MenuItem key={s} value={s}>
												{orderStatusTranslationConformity[s]}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</div>
							<div style={{ minWidth: 200 }}>
								<FormControl fullWidth>
									<InputLabel id="demo-simple-select-label">
										Тип доставки
									</InputLabel>
									<Select
										labelId="demo-simple-select-label"
										id="delivery-type-select"
										value={deliveryType}
										label="Тип доставки"
										onChange={(event) => setDeliveryType(event.target.value)}
									>
										<MenuItem key="emptyitem" value="">
											ВСІ
										</MenuItem>
										{Object.keys(deliveryTypeTranslationConformity).map((s) => (
											<MenuItem key={s} value={s}>
												{deliveryTypeTranslationConformity[s]}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</div>
							<div style={{ marginRight: 20 }}>
								<FormControlLabel
									label="Лише архівні"
									control={
										<Checkbox
											checked={archive}
											onChange={(event) => {
												setArchive((oldState) => !oldState);
											}}
										/>
									}
								/>
							</div>
							{authScope === UserRoles.ADMIN && (
								<div style={{}}>
									<Button
										onClick={() => {
											setAddingNewOrder((oldState) => !oldState);
											setEditableRowId(emptyOrder.id);
										}}
										color={addingNewOrder ? "secondary" : "primary"}
										variant="contained"
									>
										Створити нову
									</Button>
								</div>
							)}
							{authScope === UserRoles.MANAGER && (
								<div style={{}}>
									<FormControlLabel
										label="Лише мої"
										control={
											<Checkbox
												checked={onlyMy}
												onChange={(event) => {
													setOnlyMy((oldState) => !oldState);
												}}
											/>
										}
									/>
								</div>
							)}
						</Box>
						{/* Bottom nav panel */}
						{!isLoading() && authScope === UserRoles.ADMIN && (
							<Box
								sx={{
									display: "flex",
									justifyContent: "flex-start",
									alignItems: "center",
									marginLeft: 1,
									// margin: 10,
									gap: 2,
								}}
							>
								<Box sx={{ minWidth: 200 }}>
									<FormControl fullWidth>
										<InputLabel id="demo-simple-select-label">Склад</InputLabel>
										<Select
											labelId="demo-simple-select-label"
											id="warehouse-select"
											value={warehouse}
											label="Склад"
											onChange={(event) => setWarehouse(event.target.value)}
										>
											<MenuItem key="emptyitem" value="">
												ВСІ
											</MenuItem>
											{getWarehousesData.warehouses.map((w) => (
												<MenuItem key={w.id} value={w.id}>
													{w.city}&nbsp; - &nbsp;{w.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>
								<Box sx={{ minWidth: 250 }}>
									{getUsersForSelectData &&
										getUsersForSelectData.users &&
										getUsersForSelectData.users.users && (
											<Autocomplete
												options={getUsersForSelectData.users.users}
												getOptionLabel={(option) => option.name}
												id="clear-on-escape"
												clearOnEscape
												value={user}
												onChange={(e, selectedValue) => setUser(selectedValue)}
												renderInput={(params) => (
													<TextField
														{...params}
														label="ПІБ"
														variant="outlined"
													/>
												)}
											/>
										)}
									{/* <FormControl fullWidth>
											<InputLabel id="manager-select-label">
												Співробітник
											</InputLabel>
											<Select
												labelId="manager-select-label"
												id="manager-select"
												value={user}
												label="Співробітник"
												onChange={(event) => setUser(event.target.value)}
											>
												<MenuItem key="emptyitem" value="">
													ВСІ
												</MenuItem>
												{getUsersForSelectData &&
													getUsersForSelectData.users &&
													getUsersForSelectData.users.users &&
													getUsersForSelectData.users.users.map((u) => (
														<MenuItem key={u.id} value={u.id}>
															{u.name}
														</MenuItem>
													))}
											</Select>
										</FormControl> */}
								</Box>
							</Box>
						)}
					</Box>
				</Paper>
				{!isLoading() && (
					<>
						<TableContainer component={Paper}>
							<Table aria-label="orders table" stickyHeader>
								<OrderListHead
									order="asc"
									orderBy="orderNumber"
									headLabel={ordersTableColumns}
									rowCount={10}
									// numSelected={selected.length}
									// onRequestSort={handleRequestSort}
									// onSelectAllClick={handleSelectAllClick}
								/>
								{/* <TableHead>
										<TableRow>
											{ordersTableColumns.map((column) => (
												<TableCell
													key={column.id}
													align={column.align}
													style={{
														minWidth: column.minWidth,
														maxWidth: column.maxWidth,
													}}
												>
													{column.label}
												</TableCell>
											))}
										</TableRow>
									</TableHead> */}
								<TableBody>
									{addingNewOrder && (
										<OrdersEditableRow
											key={emptyOrder.id}
											row={emptyOrder}
											actions={{
												saveRow: saveRow,
												cancelEdit: cancelEditingRow,
											}}
										/>
									)}
									{data.orders.orders.map((order) =>
										isEditableRow(order.id) ? (
											<OrdersEditableRow
												key={order.id}
												row={order}
												actions={{
													saveRow: saveRow,
													tryDeleteRow: tryDeleteRow,
													cancelEdit: cancelEditingRow,
												}}
											/>
										) : (
											<OrdersRow
												key={order.id}
												authScope={authScope}
												row={order}
												buttonsActive={!editableRowId}
												actions={{
													editRow: startEditingRow,
													tryDeleteRow: tryDeleteRow,
													showNPModal: showNPModal,
												}}
											/>
										)
									)}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							labelRowsPerPage="Накладних на сторінці:"
							rowsPerPageOptions={[10, 25, 100]}
							component="div"
							count={data.orders.totalCount}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</>
				)}
				<CommonDialog
					open={!!deleteRowId}
					confirmAction={deleteRow}
					cancelAction={() => setDeleteRowId(null)}
					question={`Ви хочете видалити накладну?`}
				/>
				<NovaPoshtaDialog
					open={
						isShowNPModal &&
						getNovaPoshtaStatusesData &&
						getNovaPoshtaStatusesData?.novaPoshtaStatuses &&
						getNovaPoshtaStatusesData?.novaPoshtaStatuses.length > 0
					}
					items={getNovaPoshtaStatusesData?.novaPoshtaStatuses}
					onClose={() => setIsShowNPModal(false)}
				/>
			</div>
			{isLoading() && (
				<CircularProgress
					size={24}
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
					}}
				/>
			)}
		</>
	);
}
